import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { formValueSelector } from "redux-form";
import { EnumWrapper } from "../../../../../utils/enums";
import { LANGUAGE_CODES, PROVINCES } from "../../../../../utils/enums/enums";
import * as premiumSimulatorActions from "./premiumSimulatorActions";
import PremiumSimulatorFields from "./PremiumSimulatorFields";
import * as dataActions from "../../../../../utils/data/dataActions";
import {
  getLatestMemberCoverageForMember,
  selectMembersWithActiveCoverage,
  selectMemberOptions,
} from "./premiumSimulatorHelpers";
import { EXISTING, NEW } from "./employeeTypeChoices";
import { CLASSES } from "../../../../../utils/data/models";

const CLASS_CLIENT_COVERAGES = { ...CLASSES, name: "classClientCoverages" };

class PremiumSimulatorFormContainer extends React.Component {
  constructor(props) {
    super(props);

    this.handleOnClassChange = this.handleOnClassChange.bind(this);
    this.getAttributesForMemberId = this.getAttributesForMemberId.bind(this);

    this.state = {
      initialValues: {
        employeeType: NEW.value
      },
      latestLoadedClassId: null,
      isClassModified: false
    };
  }

  loadClassClientCoverages(classId) {
      this.props.dataActions.getDataList(
          { ...CLASS_CLIENT_COVERAGES, path: CLASSES.path + "/" + classId },
          0,
          0,
          "",
          ["name:asc"],
          [`classes.classId==${classId}`, CLASSES.archiveFilter],
          null,
          null,
         "classId,classClientCoverages(clientCoverage(clientCoverageId,ageOfPolicyDecrease,ageOfPolicyExpiration,commissionRate,guarantee(guaranteeId,guaranteeType,localizations)))"
      );
    }

  clearSimulation = () => {
    this.props.premiumSimulatorActions.clear(this.props.clientId);
  };

  getAttributesForMemberId(memberId) {
    const member =
      this.props.members.find((member) => member.memberId == memberId) || {};
    const memberCoverage = getLatestMemberCoverageForMember(member);
    const guarantees = (memberCoverage.memberCoverageGuarantees || []).map((g) => {return g;}) || [];
    return {
      birthdate: member.birthdate || "",
      classId: memberCoverage.classId || "",
      divisionId: memberCoverage.divisionId || "",
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      memberId,
      provinceCode: member.provinceCode || "",
      salary: memberCoverage.salary || "",
      guarantees: guarantees
    };
  }

  onEmployeeTypeChange = (_, newValue, previousValue) => {
    const { simulatorAttributes } = this.props;
    if (newValue !== previousValue) {
      this.setState({
        initialValues: {
          ...simulatorAttributes,
          employeeType: newValue,
          guarantees: [],
          classId: null
        },
      });
      this.clearSimulation();
    }
  };

  parseClassId(class_){
    if (class_ && typeof class_ == "object") {
      class_ = Object.values(class_)
        .filter((v) => typeof v == "string")
        .join("");
    }

    return class_;
  }

  onMemberChange = (_, newValue, previousValue) => {
    if (newValue === previousValue) return;

    const { simulatorAttributes } = this.props;

    const memberAttributes = this.getAttributesForMemberId(newValue);
    
    if (!!newValue) {
      this.setState({
        initialValues: {
          ...simulatorAttributes,
          ...memberAttributes,
        },
      });
    } else {
      this.setState({
        initialValues: {
          ...simulatorAttributes
        },
      });
    }

    this.handleOnClassChange(memberAttributes.classId);
    this.clearSimulation();
  };

  onClassChange = (value) => {
    this.handleOnClassChange(value);
    this.clearSimulation();
  }

  handleOnClassChange(id) {
    const classId = this.parseClassId(id);
    const isClassModified = classId != this.state.latestLoadedClassId;

    if (isClassModified) {
      if (!!classId) this.loadClassClientCoverages(classId);
    }

    this.setState({
      latestLoadedClassId: classId,
      isClassModified: isClassModified
    });
  }

  onSubmit = (data) => {
    const { clientId, premiumSimulatorActions, members, simulatorAttributes } = this.props;
    let memberId = data.memberId;
    const memberCoverageGuarantees = data.memberCoverageGuarantees.filter(g => g.checked);
    let memberIdentifier = null;
    
    if (simulatorAttributes.employeeType === EXISTING.value){
      const actualMember = members.find(m => m.memberId == memberId);
      memberIdentifier = actualMember.memberIdentifier;
    }

    if (typeof memberId === "string") {
      memberId = Number.parseInt(memberId);
    }

    const memberInfo = {
      memberId,
      memberIdentifier: memberIdentifier,
      firstName: data.firstName,
      lastName: data.lastName,
      provinceCode: data.provinceCode,
      birthdate: data.birthdate,
      salary: data.salary,
      memberCoverageGuarantees: this.getCoverageGuaranteesData(memberCoverageGuarantees)
    };
    const { payrollFrequency, languageCode, classId, divisionId } = data;
    const body = {
      classId: Number.parseInt(classId),
      divisionId: Number.parseInt(divisionId),
      payrollFrequency,
      languageCode,
      memberInfo,
    };
    return premiumSimulatorActions.simulate(clientId, body);
  };

  getCoverageGuaranteesData(memberCoverageGuarantees) {
    return memberCoverageGuarantees.map((c) => {
      return {
        ageOfPolicyDecrease: c.ageOfPolicyDecrease,
        ageOfPolicyExpiration: c.ageOfPolicyExpiration,
        allowance: c.allowance,
        coverageType: c.coverageType,
        formulaMspa: c.formulaMspa,
        guaranteeId: c.guaranteeId,
        invoicedClientRate: c.invoicedClientRate,
        isTypeMember: c.isTypeMember,
        volume: c.volume
      };
    });
  }

  render() {
    const {
      classOptions,
      divisionOptions,
      memberOptions,
      simulatorAttributes: { employeeType },
      submitting,
      clientStatusType
    } = this.props;
    const { initialValues } = this.state;

    return (
      <EnumWrapper
        showLoading={false}
        enums={[
          { ...LANGUAGE_CODES, format: "inputs" },
          {
            ...PROVINCES,
            query: "filters=provinces.countryCode==CA",
            format: "inputs",
          },
        ]}
      >
        <PremiumSimulatorFields
          onSubmit={this.onSubmit}
          initialValues={initialValues}
          onEmployeeTypeChange={this.onEmployeeTypeChange}
          onMemberChange={this.onMemberChange}
          onClassChange={this.onClassChange}
          clientStatusType={clientStatusType}
          classOptions={classOptions}
          divisionOptions={divisionOptions}
          memberOptions={memberOptions}
          existingEmployee={employeeType === "existing"}
          submitting={submitting}
          isClassModified={this.state.isClassModified}
        />
      </EnumWrapper>
    );
  }
}

PremiumSimulatorFormContainer.propTypes = {
  clientId: PropTypes.number,
  classOptions: PropTypes.array,
  divisionOptions: PropTypes.array,
  members: PropTypes.array,
  clientStatusType: PropTypes.string,
  memberOptions: PropTypes.array,
  simulatorAttributes: PropTypes.object,
  submitting: PropTypes.bool,
  premiumSimulatorActions: PropTypes.objectOf(PropTypes.func),
  dataActions: PropTypes.object
};

const formSelector = formValueSelector("premiumSimulator");

const mapStateToProps = (state) => {
  const clientId = state.data.clients.active.clientId;
  const classes = state.data.classes || {};
  const classOptions = (classes.items || []).map((class_) => ({
    value: class_.classId,
    text: class_.name,
  }));
  const divisions = state.data.divisions || {};
  const divisionOptions = (divisions.items || []).map((division) => ({
    value: division.divisionId,
    text: division.name,
  }));

  const simulatorAttributes = formSelector(
    state,
    "employeeType",
    "payrollFrequency",
    "languageCode",
    "classId"
  );

  const premiumSimulatorData = state.premiumSimulator[clientId] || {};
  const submitting = !!premiumSimulatorData.submitting;
  const clientStatusType = state.data.clients.active.clientStatusType;

  return {
    classOptions,
    clientId,
    divisionOptions,
    memberOptions: selectMemberOptions(state),
    members: selectMembersWithActiveCoverage(state),
    simulatorAttributes,
    clientStatusType,
    submitting,
  };
};

const mapDispatchToProps = (dispatch) => ({
  premiumSimulatorActions: bindActionCreators(
    premiumSimulatorActions,
    dispatch
  ),
  dataActions: bindActionCreators(dataActions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PremiumSimulatorFormContainer);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/premiumSimulator/PremiumSimulatorFormContainer.js